import React from "react";
import { Clock3, PieChart, FolderKanban, ArrowRightLeft, Users, Activity, Download, AlertTriangle, Check, ChevronRight, Ban, Newspaper, Sparkles } from "lucide-react";
import { GPanel, PanelHead, Pager } from "./parts";
import { useSpot } from "./WowMotion";
import { orgAlertsStream, orgNewsStream } from "../../components/drilldown/drilldownApi";
import { alertSel } from "../../components/drilldown/drilldownRoutes";
import { inks, paper } from "./viz";
import {
  PolarClock, DayCurve, ToolRings, ProjectDonut, RaceRows, FocusSankey, hourPeople, toolMinutes,
  SkelPeople, SkelDonut, SkelBars, SkelFlow,
} from "./viz";
import { ToolIcon } from "../../components/classic/panels";

function Empty({ t, children }) {
  return (
    <div style={{ padding: "26px 6px", textAlign: "center", fontSize: 12.5, color: t.c.textTertiary }}>
      {children}
    </div>
  );
}

function Card({ t, children, className, style }) {
  const spot = useSpot();
  return (
    <GPanel t={t} className={`tgw-card tgw-spot ${className || ""}`} onMouseMove={spot} style={{ overflow: "hidden", ...style }}>
      {children}
    </GPanel>
  );
}

export function ClockPanel({ t, overview, loading }) {
  const people = hourPeople(overview);
  const peak = people ? people.indexOf(Math.max(...people)) : -1;
  return (
    <Card t={t}>
      <PanelHead
        t={t}
        title="When the team is on"
        icon={<Clock3 size={15} strokeWidth={2.2} />}
        tone="blue"
        subtitle="24-hour clock"
        info="matrix"
      />
      {loading ? <SkelDonut t={t} legend={0} size={250} /> : !people ? (
        <Empty t={t}>No hourly detail for this window.</Empty>
      ) : (
        <>
          <PolarClock t={t} hourPeople={people} />
          <div style={{ fontSize: 11, color: t.c.textTertiary, padding: "0 0 4px" }}>
            Longer spoke = more of the team active{peak >= 0 ? `. Peak ${people[peak].toFixed(1)} people.` : "."}
          </div>
        </>
      )}
    </Card>
  );
}

export function CurvePanel({ t, overview, loading }) {
  const people = hourPeople(overview);
  const peak = people ? people.indexOf(Math.max(...people)) : -1;
  return (
    <Card t={t}>
      <PanelHead
        t={t}
        title="The working day"
        icon={<Activity size={15} strokeWidth={2.2} />}
        tone="violet"
        subtitle={peak >= 0 ? `peak · ${people[peak].toFixed(1)} people` : "people active, averaged over the period"}
        info="curve"
      />
      {loading ? <SkelBars t={t} height={160} /> : !people ? (
        <Empty t={t}>No hourly detail for this window.</Empty>
      ) : (
        <DayCurve t={t} hourPeople={people} />
      )}
    </Card>
  );
}

export function ToolsPanel({ t, overview, loading, onTool, matchPeople }) {
  const tools = (overview?.tools || []).filter((x) => x && x.name && toolMinutes(x) > 0);
  return (
    <Card t={t} style={matchPeople ? {
      minHeight: 420, height: "100%", display: "flex", flexDirection: "column", minWidth: 0,
    } : undefined}>
      <PanelHead t={t} title="Where hours went" icon={<PieChart size={15} strokeWidth={2.2} />} tone="violet" subtitle="share of time" info="tools" />
      <div style={matchPeople ? { flex: 1, display: "flex", minHeight: 0 } : undefined}>
        {loading ? <SkelDonut t={t} /> : !tools.length ? (
          <Empty t={t}>No tool detail for this window.</Empty>
        ) : (
          <div style={{ width: "100%", ...(matchPeople ? { flex: 1, display: "flex", minHeight: 0 } : {}) }}>
            <ToolRings t={t} tools={tools} onTool={onTool} fill={!!matchPeople} />
          </div>
        )}
      </div>
    </Card>
  );
}

export function ProjectsPanelG({ t, overview, loading, onProject, matchAlerts }) {
  const projects = (overview?.projects || []).filter((p) => p && p.name);
  return (
    <Card t={t} style={{
      height: "100%", display: "flex", flexDirection: "column", minWidth: 0, paddingBottom: 0,
      ...(matchAlerts ? { minHeight: 336 } : null),
    }}>
      <PanelHead t={t} title="Projects" icon={<FolderKanban size={15} strokeWidth={2.2} />} tone="gold" subtitle={projects.length ? `${projects.length} project${projects.length === 1 ? "" : "s"}` : "projects"} info="projectShare" />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {loading ? <SkelDonut t={t} /> : !projects.length ? (
          <Empty t={t}>No project carried time in this window.</Empty>
        ) : (
          <ProjectDonut t={t} projects={projects} onProject={onProject} />
        )}
      </div>
    </Card>
  );
}

export const ALERT_ICON = {
  blocker: Ban,
  work: AlertTriangle,
  news: Newspaper,
};

export function paintAlert(a) {
  return {
    ...a,
    tone: a.kind === "blocker" ? "gold" : "blue",
    Icon: ALERT_ICON[a.kind] || AlertTriangle,
  };
}

function alertKick(row) {
  return row.kicker && !/^(blocker|alert|on this|news|today)$/i.test(row.kicker) ? row.kicker : "";
}

function alertDoorLabel(drill) {
  if (!drill) return "";
  if (drill.name) return `Open ${drill.name}`;
  if (drill.kind === "person") return "Open person";
  if (drill.kind === "project") return "Open project";
  if (drill.kind === "day") return "Open day";
  return "Open";
}

function alertKey(row) {
  return alertSel(row);
}

export function AlertRows({ t, k, ppr, rows, onOpen, selected, rail, grid }) {
  return (
    <div className={`tgw-tl${grid ? " tgw-news-grid" : ""}`} style={{
      display: grid ? "grid" : "flex",
      flexDirection: grid ? undefined : "column",
      gridTemplateColumns: grid ? "1fr 1fr" : undefined,
      gap: rail ? 4 : grid ? 8 : 6,
      minWidth: 0,
    }}>
      {rows.map((row, i) => {
        const ink = k[row.tone] || k.gold;
        const Icon = row.Icon || AlertTriangle;
        const kick = alertKick(row);
        const on = selected && alertKey(selected) === alertKey(row);
        const wash = t.isDark
          ? `color-mix(in srgb, ${ink} ${on ? 32 : 22}%, ${ppr.well})`
          : `color-mix(in srgb, ${ink} ${on ? 22 : 14}%, #fff)`;
        return (
          <button
            key={`${row.kind}-${i}-${row.title}`}
            type="button"
            className="tgw-alert-card"
            onClick={() => onOpen?.(row)}
            style={{
              display: "grid",
              gridTemplateColumns: rail ? "3px minmax(0,1fr)" : "3px 26px minmax(0,1fr) 14px",
              gap: rail ? 8 : 10, alignItems: "center",
              width: "100%", textAlign: "left",
              border: `1px solid color-mix(in srgb, ${ink} ${on ? 55 : 38}%, ${t.c.border})`,
              padding: rail ? "8px 10px 8px 0" : "9px 10px 9px 0", borderRadius: 10,
              cursor: "pointer", fontFamily: "inherit", color: "inherit",
              background: wash,
            }}
          >
            <span aria-hidden="true" style={{ width: 3, alignSelf: "stretch", background: ink, borderRadius: "10px 0 0 10px" }} />
            {rail ? null : (
              <span aria-hidden="true" style={{
                width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                display: "grid", placeItems: "center", background: ink, color: "#fff",
              }}><Icon size={13} strokeWidth={2.2} /></span>
            )}
            <span style={{ minWidth: 0 }}>
              {kick && !rail ? <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: ink }}>{kick}</span> : null}
              <span style={{
                display: "block", marginTop: kick && !rail ? 2 : 0, fontSize: rail ? 13 : 13.5, fontWeight: 700, letterSpacing: "-0.2px",
                lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{row.title}</span>
              {rail ? (
                row.names ? (
                  <span style={{
                    display: "block", marginTop: 2, fontSize: 11.5, color: t.c.textSecondary,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{row.names}</span>
                ) : null
              ) : (
                <span style={grid ? {
                  marginTop: 3, fontSize: 12, color: t.c.textSecondary, lineHeight: 1.45,
                  overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                } : {
                  display: "block", marginTop: 3, fontSize: 12, color: t.c.textSecondary, lineHeight: 1.4,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{[row.names, row.body].filter(Boolean).join(" · ")}</span>
              )}
            </span>
            {rail ? null : <span style={{ color: ink, display: "grid" }}><ChevronRight size={14} /></span>}
          </button>
        );
      })}
    </div>
  );
}

export function AlertDetail({ t, k, ppr, row, onDrill }) {
  const ink = k[row.tone] || k.gold;
  const Icon = row.Icon || AlertTriangle;
  const kick = alertKick(row);
  const kind = row.kind === "blocker" ? "Blocker" : row.kind === "news" ? "News" : "Alert";
  const door = alertDoorLabel(row.drill);
  const wash = t.isDark
    ? `color-mix(in srgb, ${ink} 18%, ${ppr.well})`
    : `color-mix(in srgb, ${ink} 10%, #fff)`;
  return (
    <div style={{
      border: `1px solid color-mix(in srgb, ${ink} 38%, ${t.c.border})`,
      borderRadius: 12, padding: "16px 18px 18px", background: wash,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span aria-hidden="true" style={{
          width: 28, height: 28, borderRadius: 8, display: "grid", placeItems: "center",
          background: ink, color: "#fff", flexShrink: 0,
        }}><Icon size={14} strokeWidth={2.2} /></span>
        <span style={{ fontSize: 11, fontWeight: 700, color: ink, letterSpacing: "-0.05px" }}>
          {kick ? `${kind} · ${kick}` : kind}
        </span>
      </div>
      <h4 style={{ margin: 0, fontSize: 18, fontWeight: 750, letterSpacing: "-0.35px", lineHeight: 1.3 }}>
        {row.title}
      </h4>
      {row.names ? (
        <p style={{ margin: "8px 0 0", fontSize: 13.5, fontWeight: 650, color: t.c.textSecondary }}>{row.names}</p>
      ) : null}
      {row.body ? (
        <p style={{ margin: "12px 0 0", fontSize: 14, lineHeight: 1.6, color: t.c.text }}>{row.body}</p>
      ) : null}
      {door ? (
        <button
          type="button"
          onClick={() => onDrill?.(row.drill)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, marginTop: 16,
            border: 0, borderRadius: 10, padding: "8px 12px", cursor: "pointer",
            fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: "#fff",
            background: k.blue,
          }}
        >
          {door} <ChevronRight size={14} />
        </button>
      ) : null}
    </div>
  );
}

export function AlertTabs({ t, tab, onTab, blockers, alerts, flush }) {
  const k = inks(t);
  const items = [
    { id: "blocker", label: "Blockers", n: blockers, ink: k.gold },
    { id: "work", label: "Alerts", n: alerts, ink: k.blue },
  ];
  return (
    <div style={{ display: "flex", gap: 6, margin: flush ? 0 : "0 0 10px" }}>
      {items.map((x) => {
        const on = tab === x.id;
        return (
          <button
            key={x.id}
            type="button"
            onClick={() => onTab(x.id)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "5px 11px", borderRadius: 999, fontFamily: "inherit",
              fontSize: 12, fontWeight: 650, letterSpacing: "-0.1px",
              cursor: "pointer", color: on ? x.ink : t.c.textSecondary,
              background: on ? `color-mix(in srgb, ${x.ink} 14%, transparent)` : "transparent",
              border: `1px solid ${on ? `color-mix(in srgb, ${x.ink} 40%, ${t.c.border})` : t.c.border}`,
            }}
          >
            {x.label}
            <b style={{ fontVariantNumeric: "tabular-nums", fontWeight: 750 }}>{x.n}</b>
          </button>
        );
      })}
    </div>
  );
}

function alertCountLine(blockers, alerts) {
  const bits = [];
  if (blockers) bits.push(`${blockers} blocker${blockers === 1 ? "" : "s"}`);
  if (alerts) bits.push(`${alerts} alert${alerts === 1 ? "" : "s"}`);
  return bits.join(" · ");
}

export function AlertsPanel({ t, overview, loading, onOpen, admin, range, member }) {
  const k = inks(t);
  const ppr = paper(t);
  const [rows, setRows] = React.useState([]);
  const [lede, setLede] = React.useState("");
  const [empty, setEmpty] = React.useState("");
  const [writing, setWriting] = React.useState(false);
  const [tab, setTab] = React.useState("blocker");
  const ready = Boolean(admin && overview?.totals && range?.start && range?.end);
  const startKey = range?.start ? String(range.start) : "";
  const endKey = range?.end ? String(range.end) : "";

  React.useEffect(() => {
    if (!ready) return undefined;
    const ac = new AbortController();
    setRows([]);
    setLede("");
    setEmpty("");
    setTab("blocker");
    setWriting(true);
    orgAlertsStream(range, { member, signal: ac.signal }, (d) => {
      if (d.type === "lede") setLede(d.text || "");
      else if (d.type === "alert") setRows((cur) => [...cur, paintAlert(d)]);
      else if (d.type === "empty" || d.type === "error") setEmpty(d.message || "");
      else if (d.type === "done") setWriting(false);
    }).catch((e) => {
      if (e.name !== "AbortError" && !ac.signal.aborted) setEmpty(e.message || "Could not write alerts.");
    }).finally(() => { if (!ac.signal.aborted) setWriting(false); });
    return () => ac.abort();
  }, [ready, startKey, endKey, member, range]);

  const blockers = rows.filter((r) => r.kind === "blocker");
  const alerts = rows.filter((r) => r.kind !== "blocker");
  React.useEffect(() => {
    if (writing) return;
    if (tab === "blocker" && !blockers.length && alerts.length) setTab("work");
  }, [writing, tab, blockers.length, alerts.length]);

  if (!admin) return null;

  const shown = tab === "blocker" ? blockers : alerts;
  const busy = (loading || writing) && !rows.length && !empty;
  const counts = alertCountLine(blockers.length, alerts.length);
  const sub = writing && !rows.length ? "reading work…" : counts || (empty ? "none yet" : "clear");
  return (
    <Card t={t} style={{ minHeight: 336, height: "100%", display: "flex", flexDirection: "column", minWidth: 0 }}>
      <PanelHead
        t={t}
        title="Needs attention"
        icon={<AlertTriangle size={15} strokeWidth={2.2} />}
        tone="gold"
        subtitle={sub}
        info="attention"
      />
      <AlertTabs t={t} tab={tab} onTab={setTab} blockers={blockers.length} alerts={alerts.length} />
      {busy ? (
        <div style={{ padding: "2px 4px 4px", fontSize: 13, lineHeight: 1.5, color: t.c.textSecondary }}>
          Reading the work — blockers and alerts land as they come.
        </div>
      ) : !shown.length ? (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "2px 4px 4px" }}>
          <span aria-hidden="true" style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            display: "grid", placeItems: "center", background: k.teal, color: "#fff",
          }}><Check size={16} strokeWidth={2.2} /></span>
          <span>
            <span style={{ display: "block", fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px" }}>
              {tab === "blocker" ? "No blockers" : "No alerts"}
            </span>
            <span style={{ display: "block", marginTop: 6, fontSize: 13, lineHeight: 1.5, color: t.c.textSecondary }}>
              {empty || lede || (tab === "blocker"
                ? "No named blocker from this window’s work yet."
                : "No named work to flag in this window yet.")}
            </span>
          </span>
        </div>
      ) : (
        <div className="tgw-alerts-scroll" style={{ flex: 1, minHeight: 0, maxHeight: 248 }}>
          {lede ? (
            <div style={{ fontSize: 12.5, lineHeight: 1.4, color: t.c.textSecondary, margin: "0 0 8px" }}>{lede}</div>
          ) : null}
          <AlertRows t={t} k={k} ppr={ppr} rows={shown} onOpen={onOpen} />
        </div>
      )}
    </Card>
  );
}

function newsTitle(range) {
  if (range?.key === "today") return "Today's story";
  if (range?.key === "yesterday") return "Yesterday's story";
  return "The story";
}

export function NewsPanel({ t, overview, loading, onOpen, admin, range, member }) {
  const k = inks(t);
  const ppr = paper(t);
  const [rows, setRows] = React.useState([]);
  const [lede, setLede] = React.useState("");
  const [empty, setEmpty] = React.useState("");
  const [writing, setWriting] = React.useState(false);
  const ready = Boolean(admin && overview?.totals && range?.start && range?.end);
  const startKey = range?.start ? String(range.start) : "";
  const endKey = range?.end ? String(range.end) : "";
  const viewKey = `${startKey}|${endKey}|${member || ""}`;
  const title = newsTitle(range);
  const viewRef = React.useRef(viewKey);

  React.useEffect(() => {
    if (!ready) return undefined;
    const ac = new AbortController();
    const switched = viewRef.current !== viewKey;
    viewRef.current = viewKey;
    if (switched) {
      setRows([]);
      setLede("");
    }
    setEmpty("");
    setWriting(true);
    let got = false;
    orgNewsStream(range, { member, signal: ac.signal }, (d) => {
      if (d.type === "lede") setLede(d.text || "");
      else if (d.type === "alert") {
        const row = paintAlert({ ...d, kind: "news" });
        setRows((cur) => (got ? [...cur, row] : [row]));
        got = true;
      } else if (d.type === "empty" || d.type === "error") {
        if (got) return;
        const msg = d.message || "";
        setEmpty(/already written/i.test(msg) ? "" : msg);
      } else if (d.type === "done") setWriting(false);
    }).catch((e) => {
      if (e.name !== "AbortError" && !ac.signal.aborted && !got) setEmpty(e.message || "Could not write today's news.");
    }).finally(() => { if (!ac.signal.aborted) setWriting(false); });
    return () => ac.abort();
  }, [ready, startKey, endKey, member, range, viewKey]);

  if (!admin) return null;

  const busy = (loading || writing) && !rows.length && !empty;
  const sub = writing && !rows.length
    ? "reading the day…"
    : rows.length
      ? `${rows.length} stor${rows.length === 1 ? "y" : "ies"}`
      : empty ? "none yet" : "from the written record";

  return (
    <Card t={t} style={{ minHeight: 220, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <PanelHead
        t={t}
        title={title}
        icon={<Newspaper size={15} strokeWidth={2.2} />}
        tone="blue"
        subtitle={sub}
        info="alerts"
      />
      {busy ? (
        <div style={{ padding: "2px 4px 8px", fontSize: 13, lineHeight: 1.5, color: t.c.textSecondary }}>
          Reading the day's work — stories land as they come.
        </div>
      ) : !rows.length ? (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "2px 4px 8px" }}>
          <span aria-hidden="true" style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            display: "grid", placeItems: "center", background: k.teal, color: "#fff",
          }}><Check size={16} strokeWidth={2.2} /></span>
          <span>
            <span style={{ display: "block", fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px" }}>
              Quiet so far
            </span>
            <span style={{ display: "block", marginTop: 6, fontSize: 13, lineHeight: 1.5, color: t.c.textSecondary }}>
              {empty || lede || "No named story from this window’s work yet."}
            </span>
          </span>
        </div>
      ) : (
        <div className="tgw-alerts-scroll" style={{ flex: 1, minHeight: 0, maxHeight: 340 }}>
          {lede ? (
            <div style={{ fontSize: 14, lineHeight: 1.45, fontWeight: 650, letterSpacing: "-0.2px", margin: "0 0 10px" }}>
              {lede}
            </div>
          ) : null}
          <AlertRows t={t} k={k} ppr={ppr} rows={rows} onOpen={onOpen} grid />
        </div>
      )}
    </Card>
  );
}

const fmtH = (m) => {
  const v = Math.max(0, Math.round(Number(m) || 0));
  const h = Math.floor(v / 60);
  return h ? `${h}h ${String(v % 60).padStart(2, "0")}m` : `${v}m`;
};

export function AiAdoptionPanel({ t, data, loading, onPerson }) {
  const k = inks(t);
  const people = data?.people || [];
  const org = data?.org;
  const max = Math.max(...people.map((p) => p.aiMin || 0), 1);
  const sub = loading
    ? "reading the roster…"
    : org
      ? `${org.adopters}/${org.people} people · ${fmtH(org.aiMin)} · ${org.share}%`
      : "none yet";

  return (
    <Card t={t} style={{ minHeight: 336, height: "100%", display: "flex", flexDirection: "column", minWidth: 0 }}>
      <PanelHead
        t={t}
        title="AI adoption"
        icon={<Sparkles size={15} strokeWidth={2.2} />}
        tone="violet"
        subtitle={sub}
        info="attention"
      />
      {loading ? (
        <SkelPeople t={t} count={6} />
      ) : !people.length ? (
        <Empty t={t}>No one sat in a named AI tool in this window.</Empty>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", maxHeight: 280 }}>
          {data?.blurb ? (
            <div style={{ fontSize: 12.5, lineHeight: 1.45, color: t.c.textSecondary, margin: "0 0 10px" }}>
              {data.blurb}
            </div>
          ) : null}
          {people.map((p) => {
            const w = Math.max(8, (p.aiMin / max) * 100);
            const ini = String(p.name || "").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
            const av = t.accentFor ? t.accentFor(String(p.name || "")) : null;
            return (
              <button
                key={p.userId}
                type="button"
                className="tgw-row"
                onClick={() => onPerson?.(p)}
                style={{
                  display: "grid", gridTemplateColumns: "22px 1fr", gap: 8, alignItems: "center",
                  width: "100%", padding: "6px 4px", textAlign: "left", border: 0, background: "none",
                  cursor: onPerson ? "pointer" : "default", fontFamily: "inherit", color: "inherit",
                }}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: 99, display: "grid", placeItems: "center",
                  fontSize: 8, fontWeight: 800, flexShrink: 0,
                  background: av ? av.bg : t.c.primarySoft, color: av ? av.fg : k.violet,
                }}>{ini}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12, marginBottom: 3 }}>
                    <b style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</b>
                    <span style={{ flex: "none", fontVariantNumeric: "tabular-nums", color: t.c.textSecondary }}>
                      {fmtH(p.aiMin)} · {p.share}%
                    </span>
                  </span>
                  <span style={{ display: "block", height: 5, borderRadius: 99, background: t.c.surfaceHover, overflow: "hidden" }}>
                    <i className="tgw-growX" style={{ display: "block", height: "100%", width: `${w}%`, background: k.violet, borderRadius: 99 }} />
                  </span>
                  {(p.tools || []).length ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, minWidth: 0 }}>
                      {(p.tools || []).map((x) => (
                        <ToolIcon key={x.key || x.label} name={x.label} size={16} />
                      ))}
                      <span style={{
                        fontSize: 11, color: t.c.textTertiary, minWidth: 0,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {(p.tools || []).map((x) => x.label).join(" · ")}
                      </span>
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export function FlowPanel({ t, overview, loading, onTool }) {
  const flow = overview?.detail?.flow;
  const cov = overview?.detail?.coverage;
  if (!loading && (!flow || !flow.pairs?.length)) return null;
  return (
    <Card t={t}>
      <PanelHead
        t={t}
        title="How focus moved"
        icon={<ArrowRightLeft size={15} strokeWidth={2.2} />}
        tone="pink"
        subtitle={cov ? `${cov.covered} of ${cov.total}` : "how focus moved"}
        info="flow"
      />
      {loading ? <SkelFlow t={t} /> : (
        <FocusSankey t={t} pairs={flow.pairs} onTool={onTool} />
      )}
    </Card>
  );
}

/**
 * Server-side paged: the caller owns the cursor (useGraphicalPeople fetches
 * one page at a time) and passes page/pages/onPage through. `pageSize` only
 * shapes the "from–to of N" line.
 */
export function PeoplePanel({ t, people, total, loading, onPerson, onIdle, page = 0, pages = 1, onPage, pageSize = 16, onExport }) {
  const count = people?.length || 0;
  const from = page * pageSize + 1;
  const to = page * pageSize + count;
  return (
    <Card t={t} style={{ height: "100%", display: "flex", flexDirection: "column", minWidth: 0, paddingBottom: pages > 1 ? 0 : undefined }}>
      <PanelHead
        t={t}
        title="Team Activity Log"
        icon={<Users size={15} strokeWidth={2.2} />}
        tone="blue"
        subtitle={count ? `${from}–${to} of ${total || count}` : null}
        info="people"
        cue={count && onExport ? (
          <button
            type="button"
            onClick={onExport}
            title="Export the full team to Excel"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              border: `1px solid ${t.c.border}`, background: t.c.surface,
              borderRadius: 8, padding: "5px 9px", fontSize: 11.5, fontWeight: 550,
              color: t.c.textSecondary, cursor: "pointer", fontFamily: "inherit",
            }}
          >
            <Download size={12} /> Export
          </button>
        ) : (count ? "click a row →" : null)}
      />
      {loading ? <SkelPeople t={t} count={8} /> : !count ? (
        <Empty t={t}>No one tracked time in this window.</Empty>
      ) : (
        <>
          <div style={{ flex: 1, minHeight: 0 }}>
            <RaceRows t={t} people={people} onPerson={onPerson} onIdle={onIdle} columns={2} />
          </div>
          {onPage ? <Pager t={t} page={page} pages={pages} onPage={onPage} flush /> : null}
        </>
      )}
    </Card>
  );
}

/* leftover names so older imports don't explode */
export const MatrixPanel = ClockPanel;
export const RhythmPanel = ClockPanel;
